import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable, Subscription, take } from 'rxjs';
import { LoggerService } from '../logger/logger.service';

/**
 * Plays agent TTS audio from a stream.
 * Consumes an Observable of audio data (e.g. from CallService.getAudioStream),
 * plays it via HTMLAudioElement, and exposes lifecycle events for turn-taking in AudioCallControlService.
 */
@Injectable({
  providedIn: 'root',
})
export class TtsStreamService implements OnDestroy {
  private audioElement: HTMLAudioElement | null = null;
  private currentAudioUrl: string | null = null;
  private streamSubscription: Subscription | null = null;
  private playRequestId = 0;
  private activePlayRequestId: number | null = null;
  private activeAudioSrc: string | null = null;
  private stopToken = 0;
  private ignoreOnError = false;

  private readonly speakingStart$ = new Subject<void>();
  private readonly speakingEnd$ = new Subject<void>();
  private readonly error$ = new Subject<Error>();

  readonly onSpeakingStart$: Observable<void> = this.speakingStart$.asObservable();
  readonly onSpeakingEnd$: Observable<void> = this.speakingEnd$.asObservable();
  readonly onError$: Observable<Error> = this.error$.asObservable();

  isSpeaking = false;
  isLoading = false;

  constructor(private readonly logger: LoggerService) {
    this.initAudioElement();
  }

  private initAudioElement(): void {
    this.audioElement = new Audio();

    this.audioElement.onplay = () => {
      this.isSpeaking = true;
      this.isLoading = false;
      this.ignoreOnError = false;
      this.speakingStart$.next();
      this.logger.log('[TTS_STREAM] Audio started playing');
    };

    this.audioElement.onended = () => {
      this.isSpeaking = false;
      this.speakingEnd$.next();
      this.logger.log('[TTS_STREAM] Audio finished playing');
      this.cleanup();
    };

    this.audioElement.onerror = () => {
      const isStaleError =
        !!this.activeAudioSrc &&
        !!this.audioElement?.currentSrc &&
        this.audioElement.currentSrc !== this.activeAudioSrc;
      if (this.ignoreOnError || isStaleError) {
        this.logger.warn('[TTS_STREAM] Ignoring audio error during stop/stale playback', {
          isStaleError,
          ignoreOnError: this.ignoreOnError,
        });
        return;
      }
      this.logger.error(
        '[TTS_STREAM] Audio playback error',
        new Error('Audio playback failed'),
        {
          audioCurrentSrc: this.audioElement?.currentSrc ?? '',
          activeAudioSrc: this.activeAudioSrc,
          audioErrorCode: this.audioElement?.error?.code ?? null,
        }
      );
      this.isSpeaking = false;
      this.isLoading = false;
      this.error$.next(new Error('Audio playback failed'));
      this.cleanup();
    };
  }

  /**
   * Consumes the audio stream and plays it.
   * Expects a single emission (full buffer) from the backend; multiple chunks can be
   * supported later via concatenation or MSE.
   * Subscription is stored so stop() can unsubscribe and abort in-flight requests.
   */
  playStream(stream$: Observable<ArrayBuffer>): void {
    this.playRequestId += 1;
    this.activePlayRequestId = this.playRequestId;
    const requestId = this.playRequestId;
    this.stop();
    this.isLoading = true;
    this.logger.log('[TTS_STREAM] Consuming stream');

    this.streamSubscription = stream$.pipe(take(1)).subscribe({
      next: (buffer) => this.playBuffer(buffer, requestId),
      error: (err) => {
        this.streamSubscription = null;
        this.isLoading = false;
        this.isSpeaking = false;
        this.ignoreOnError = false;
        this.logger.error(
          '[TTS_STREAM] Stream error',
          err as Error
        );
        this.error$.next(err instanceof Error ? err : new Error(String(err)));
        this.cleanup();
      },
      complete: () => {
        this.streamSubscription = null;
      },
    });
  }

  private playBuffer(buffer: ArrayBuffer, requestId: number): void {
    this.streamSubscription = null;
    if (this.activePlayRequestId !== requestId) {
      this.logger.warn('[TTS_STREAM] Ignoring stale buffer', {
        requestId,
        activePlayRequestId: this.activePlayRequestId,
      });
      this.ignoreOnError = false;
      return;
    }

    if (!buffer || buffer.byteLength === 0) {
      this.logger.warn('[TTS_STREAM] Empty audio buffer received', { requestId });
      this.isLoading = false;
      this.ignoreOnError = false;
      return;
    }

    const blob = new Blob([buffer], { type: 'audio/mpeg' });
    this.currentAudioUrl = URL.createObjectURL(blob);
    this.logger.log('[TTS_STREAM] Stream received, starting playback', {
      byteLength: buffer.byteLength,
    });

    if (this.audioElement) {
      this.audioElement.src = this.currentAudioUrl;
      this.activeAudioSrc = this.currentAudioUrl;
      this.audioElement.play().catch((err) => {
        this.isLoading = false;
        this.isSpeaking = false;
        this.logger.error('[TTS_STREAM] Play failed', err as Error);
        this.error$.next(err instanceof Error ? err : new Error(String(err)));
        this.cleanup();
      });
    }
  }

  stop(): void {
    ++this.stopToken;
    this.ignoreOnError = true;
    this.streamSubscription?.unsubscribe();
    this.streamSubscription = null;
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
      this.audioElement.removeAttribute('src');
      this.activeAudioSrc = null;
    }
    this.isSpeaking = false;
    this.isLoading = false;
    this.cleanup();
  }

  private cleanup(): void {
    if (this.currentAudioUrl) {
      URL.revokeObjectURL(this.currentAudioUrl);
      this.currentAudioUrl = null;
    }
  }

  ngOnDestroy(): void {
    this.stop();
    if (this.audioElement) {
      this.audioElement.onplay = null;
      this.audioElement.onended = null;
      this.audioElement.onerror = null;
      this.audioElement = null;
    }
    this.speakingStart$.complete();
    this.speakingEnd$.complete();
    this.error$.complete();
  }
}
